import { useEffect } from 'react';
import { useT, useFormatDate } from '../i18n';

export interface AppointmentDetail {
  id: number;
  appointment_date: string;
  type_name: string | null;
  type_code: string | null;
  start_time: string | null;
  end_time: string | null;
  staff_name: string | null;
  comment: string | null;
}

interface Props {
  appointment: AppointmentDetail | null;
  onClose: () => void;
}

export function AppointmentDetailModal({ appointment, onClose }: Props) {
  const { t } = useT();
  const fd = useFormatDate();
  useEffect(() => {
    if (!appointment) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [appointment, onClose]);

  if (!appointment) return null;

  const timeRange = appointment.start_time
    ? `${appointment.start_time.slice(0, 5)}${appointment.end_time ? ` – ${appointment.end_time.slice(0, 5)}` : ''}`
    : null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 'var(--s16)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          width: 'min(720px, 100%)',
          maxHeight: '90vh',
          borderRadius: 'var(--r4)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            padding: 'var(--s12) var(--s16)',
            borderBottom: '1px solid var(--line)',
            gap: 'var(--s12)',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 'var(--t-13)', fontWeight: 600, color: 'var(--ink-1)' }}>
              {fd.long(appointment.appointment_date)}
            </div>
            <div style={{ marginTop: 4, display: 'flex', gap: 'var(--s8)', flexWrap: 'wrap', alignItems: 'center', fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}>
              {appointment.type_name && (
                <span className="badge badge-sm" style={{ background: 'color-mix(in srgb, var(--accent) 18%, var(--surface))', color: 'var(--accent)' }}>
                  {appointment.type_name}
                </span>
              )}
              {timeRange && <span>{timeRange}</span>}
              {appointment.staff_name && <span>· {appointment.staff_name}</span>}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: 'var(--s4) var(--s8)',
              fontSize: 'var(--t-12)',
              border: '1px solid var(--line)',
              borderRadius: 'var(--r2)',
              background: 'var(--surface)',
              color: 'var(--ink-2)',
              cursor: 'pointer',
            }}
          >
            {t('common.close')}
          </button>
        </header>

        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--s16)' }}>
          {appointment.comment ? (
            <div
              style={{
                fontSize: 'var(--t-12)',
                color: 'var(--ink-1)',
                lineHeight: 1.55,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'inherit',
              }}
            >
              {appointment.comment}
            </div>
          ) : (
            <div style={{ fontSize: 'var(--t-12)', color: 'var(--ink-3)', fontStyle: 'italic' }}>
              {t('appointment.noComment')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
